import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, errorMessage, serverNow, unwrap, type EventChange, type EventData, type Poi, type Restriction, type RestrictionKind } from "../../api/client";
import { useSession } from "../../auth/session";
import { formatDateTime, formatTime, fromParisInput, toParisInput } from "../../domain/format";
import { foreignChanges } from "../../domain/live";
import { phase } from "../../domain/phase";
import { nextTransition, transitions } from "../../domain/status";
import { fr } from "../../i18n/fr";
import { EMPTY, type FC, type LatLonPt } from "../../map/RouteMap";
import { FALLBACK, loadSymbology, type Styles } from "../../map/symbology";
import { Notice, PhaseBadge, StatusBadge, useToast } from "../../ui/components";
import { usePackAreas } from "../../map/packs";
import { EditMap, type DrawMode, type EditMapHandle, type Selection } from "../editor/EditMap";
import { hitDetail, SearchBox } from "../editor/SearchBox";
import { categoryEntries, dotSwatch, effectEntries, MapLegend, type LegendGroup } from "../../map/Legend";
import { CategoriesPanel } from "../pois/CategoriesPanel";
import { PoiForm } from "../pois/PoiForm";
import { RestrictionForm } from "../restrictions/RestrictionForm";
import { HistoryPanel } from "../history/HistoryPanel";
import { SettingsPanel } from "./SettingsPanel";
import { SharePanel } from "./SharePanel";
import { editorRestrictionsFc, eventPoints, fc, geometryPoints, hcFc, isActiveNow, outsideZone, poisFc, polygonFc, toLine, toPoint, toPolygon } from "./geo";
import { ObjectList } from "./ObjectList";
import { useLiveEvent } from "./useLiveEvent";

// the import dialog and its parsers are loaded the first time an import starts (SW-NF-03)
const ImportDialog = lazy(() => import("../imports/ImportDialog").then((m) => ({ default: m.ImportDialog })));

type Tab = "objects" | "categories" | "history" | "settings" | "share";
const TABS: Tab[] = ["objects", "categories", "history", "settings", "share"];
const LAYER_KEY = "secoursnav.editLayers";
const DEFAULT_VISIBLE = { effects: true, drawn: true, pois: true, hc: true, zone: true };
/** kinds placed by a click on the map */
const pointKind = (k: RestrictionKind) => k === "BARRIER" || k === "HEIGHT_LIMIT" || k === "CROSSING_POINT";

/**
 * Object being drawn: kind chosen before the geometry; `existing` when the geometry of an object is redrawn;
 * `preset` carries the fields of a restriction created "next" to another one (SW-RS-21).
 */
type Draft =
  | { kind: "restriction"; restrictionKind: RestrictionKind; geometry?: Restriction["geometry"]; existing?: Restriction; preset?: Partial<Restriction> }
  | { kind: "poi"; location?: { type: "Point"; coordinates: number[] }; existing?: Poi };

export function EventWorkspace() {
  const { id, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const s = useSession();
  const toast = useToast();
  const me = s.user?.displayName;
  // SW-WS-03: what the others changed is announced
  const onChanges = useCallback((changes: EventChange[]) => {
    const foreign = foreignChanges(changes, me);
    if (foreign.length === 0) return;
    const who = [...new Set(foreign.map((c) => c.actor))].join(", ");
    if (foreign.length === 1) {
      const c = foreign[0];
      const payload = (c.payload ?? {}) as { label?: string; name?: string };
      const what = c.entityType === "EVENT" ? fr.history.entity.EVENT : `${fr.history.entity[c.entityType] ?? c.entityType}${payload.label || payload.name ? ` « ${payload.label ?? payload.name} »` : ""}`;
      toast.show(c.operation === "DELETE" ? fr.workspace.deletedBy(what, who) : fr.workspace.changedBy(what, who));
    } else toast.show(fr.workspace.changedByMany(foreign.length, who));
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = useLiveEvent(id, onChanges);
  const e = live.event;
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : "objects";
  const setTab = (t: Tab) => navigate({ pathname: `/events/${id}${t === "objects" ? "" : "/" + t}`, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });

  const [symbology, setSymbology] = useState<Styles>(FALLBACK);
  useEffect(() => { loadSymbology().then(setSymbology); }, []);
  const [selection, setSelection] = useState<Selection>(null);
  /** new position typed in the form of a selected POI (SW-PO-04): same form, its other fields stay as typed */
  const [poiMoved, setPoiMoved] = useState<{ id: string; location: Poi["location"] } | null>(null);
  useEffect(() => { setPoiMoved(null); }, [selection]);
  const [mode, setMode] = useState<DrawMode>("select");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftCount, setDraftCount] = useState(0);
  const [zoneDrawing, setZoneDrawing] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<FC | null>(null);
  const [flash, setFlash] = useState<FC | undefined>(undefined);
  const [visible, setVisible] = useState(() => { try { return { ...DEFAULT_VISIBLE, ...JSON.parse(localStorage.getItem(LAYER_KEY) ?? "{}") }; } catch { return DEFAULT_VISIBLE; } });
  useEffect(() => localStorage.setItem(LAYER_KEY, JSON.stringify(visible)), [visible]);
  const mapRef = useRef<EditMapHandle>(null);
  // event without zone nor object: no pack yet, the basemap follows the view
  const packAreas = usePackAreas();
  const readOnly = e?.status === "ARCHIVED";
  const canWrite = !readOnly && s.can("restriction.write");

  // SW-WS-10, SW-WS-12: simulated time of the workspace (statuses, effects), kept in the URL
  const atParam = params.get("at");
  const simulated = atParam !== null;
  const setAt = (iso: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (iso) n.set("at", iso); else n.delete("at"); return n; }, { replace: true });
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 60_000); return () => clearInterval(t); }, []);
  const at = useMemo(() => (atParam ? new Date(atParam) : serverNow()), [atParam, tick]);
  const atIso = at.toISOString();

  // network effects of the current revision (SW-WS-31), when the user may test routes
  const [overlay, setOverlay] = useState<FC>(EMPTY);
  // the layer is reloaded at each status transition computed locally (SW-SY-03), the 60 s tick is a safety net
  useEffect(() => {
    if (!e || simulated) return;
    const next = nextTransition(e.restrictions ?? [], e, serverNow());
    if (!next) return;
    const t = setTimeout(() => setTick((x) => x + 1), Math.min(Math.max(next.getTime() - serverNow().getTime(), 0) + 500, 2_147_000_000));
    return () => clearTimeout(t);
  }, [e, tick, simulated]);
  const atMinute = atIso.slice(0, 16);
  useEffect(() => {
    if (!id || !e || !s.can("route.test") || (e.restrictions ?? []).length === 0) { setOverlay(EMPTY); return; }
    const now = at;
    const query = !simulated && (now.getTime() < Date.parse(e.startAt) || now.getTime() >= Date.parse(e.endAt)) ? e.startAt : now.toISOString();
    api.GET("/events/{id}/overlay", { params: { path: { id }, query: { at: query } } })
      .then((r) => setOverlay(unwrap(r).features as FC))
      .catch(() => setOverlay(EMPTY));
  }, [id, e?.revision, e?.heightCorrectionsRevision, atMinute]); // eslint-disable-line react-hooks/exhaustive-deps

  // fit once per event
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    if (!e || fitted.current === e.id) return;
    const pts = eventPoints(e);
    if (pts.length) { mapRef.current?.fit(pts); fitted.current = e.id; }
  }, [e]);
  // import preview: the map shows what the file contains (SW-IM-03)
  useEffect(() => {
    if (!importPreview || importPreview.features.length === 0) return;
    const pts: [number, number][] = [];
    for (const f of importPreview.features) pts.push(...geometryPoints(f.geometry as { type: string; coordinates: unknown }));
    if (pts.length) mapRef.current?.fit(pts);
  }, [importPreview]);

  // snapped line computed by the open restriction form, drawn for a course (1.1.1)
  const [snapLine, setSnapLine] = useState<GeoJSON.Geometry | null>(null);
  const drawnFc = useMemo(() => {
    if (!e) return EMPTY;
    const pending = draft?.kind === "restriction" && draft.geometry ? { kind: draft.restrictionKind, geometry: draft.geometry, existingId: draft.existing?.id } : undefined;
    return editorRestrictionsFc(e.restrictions ?? [], selection?.type === "restriction" ? selection.id : null, overlay, snapLine, pending);
  }, [e, selection, draft, overlay, snapLine]);
  const poisData = useMemo(() => {
    if (!e) return EMPTY;
    const base = poisFc(e.pois ?? [], e.poiCategories, selection?.type === "poi" ? selection.id : null);
    if (draft?.kind === "poi" && draft.location) {
      const pending: GeoJSON.Feature = { type: "Feature", properties: { id: "__draft", name: draft.existing?.name ?? "", color: "#2f9e44", selected: true }, geometry: draft.location as GeoJSON.Geometry };
      return fc([...base.features.filter((f) => f.properties?.id !== draft.existing?.id), pending]);
    }
    if (poiMoved && selection?.type === "poi" && selection.id === poiMoved.id) {
      return fc(base.features.map((f) => (f.properties?.id === poiMoved.id ? { ...f, geometry: poiMoved.location as GeoJSON.Geometry } : f)));
    }
    return base;
  }, [e, selection, draft, poiMoved]);
  const hcData = useMemo(() => (e ? hcFc(e.heightCorrections ?? [], null) : EMPTY), [e]);
  const zoneData = useMemo(() => polygonFc(e?.zone), [e?.zone]);
  const zoneAdjData = useMemo(() => polygonFc(e?.zoneAdjusted), [e?.zoneAdjusted]);
  const trans = useMemo(() => (e ? transitions(e.restrictions ?? [], e) : []), [e]);

  const startRestriction = (k: RestrictionKind, preset?: Partial<Restriction>) => {
    setSelection(null);
    // a crossing point of the only course of the event belongs to it (1.3.0)
    const courses = (e?.restrictions ?? []).filter((r) => r.kind === "COURSE");
    const p = k === "CROSSING_POINT" && courses.length === 1 && !preset?.courseId ? { ...preset, courseId: courses[0].id } : preset;
    setDraft({ kind: "restriction", restrictionKind: k, preset: p });
    setMode(pointKind(k) ? "point" : "path");
  };
  const startPoi = () => { setSelection(null); setDraft({ kind: "poi" }); setMode("poi"); };
  const startZone = () => { setSelection(null); setDraft(null); setZoneDrawing(true); setMode("polygon"); setTab("settings"); };
  const stopDrawing = () => { setMode("select"); setZoneDrawing(false); setDraftCount(0); };

  const onPoint = useCallback((p: LatLonPt) => {
    setDraft((d) => {
      if (d?.kind === "restriction") return { ...d, geometry: toPoint(p) };
      if (d?.kind === "poi") return { ...d, location: toPoint(p) };
      return d;
    });
    setMode("select");
  }, []);
  const onPath = useCallback((pts: LatLonPt[]) => { setDraft((d) => (d?.kind === "restriction" ? { ...d, geometry: toLine(pts) } : d)); setMode("select"); setDraftCount(0); }, []);
  const [zonePending, setZonePending] = useState<ReturnType<typeof toPolygon> | null>(null);
  const onPolygon = useCallback((pts: LatLonPt[]) => { setZonePending(toPolygon(pts)); setMode("select"); setZoneDrawing(false); setDraftCount(0); }, []);

  if (live.deleted) return <div className="page"><Notice kind="warn">{fr.errors.EVENT_NOT_FOUND}</Notice><Link to="/">{fr.common.back}</Link></div>;
  if (live.loadError && !e) return <div className="page"><div className="error">{errorMessage(live.loadError, fr.errors)}</div><Link to="/">{fr.common.back}</Link></div>;
  if (!e || !id) return <div className="page muted">{fr.common.loading}</div>;

  const activeNow = isActiveNow(e, serverNow());
  const outside = outsideZone(e);
  const selectedRestriction = selection?.type === "restriction" ? e.restrictions?.find((r) => r.id === selection.id) ?? null : null;
  const selectedPoi = selection?.type === "poi" ? e.pois?.find((p) => p.id === selection.id) ?? null : null;
  const selectedHc = selection?.type === "hc" ? e.heightCorrections?.find((h) => h.id === selection.id) ?? null : null;

  const afterWrite = async (msg?: string) => { await live.sync(); if (msg) toast.show(msg); };
  /** SW-RS-12: the saved geometry is highlighted for three seconds */
  const flashGeometry = (g: { type: string; coordinates: unknown } | undefined) => {
    if (g) setFlash(fc([{ type: "Feature", properties: {}, geometry: g as GeoJSON.Geometry }]));
  };
  /** SW-RS-03: a snapping candidate was picked, the point moves there (a selected object becomes a draft) */
  const moveRestrictionTo = (p: LatLonPt, existing?: Restriction) => {
    setDraft((d) => (d?.kind === "restriction" ? { ...d, geometry: toPoint(p) } : existing ? { kind: "restriction", restrictionKind: existing.kind, existing, geometry: toPoint(p) } : d));
    if (existing) setSelection(null);
    mapRef.current?.flyTo(p, 18);
  };
  /** SW-PO-04: coordinates typed or address picked in the POI form */
  const movePoiTo = (p: LatLonPt, existing?: Poi) => {
    if (existing) setPoiMoved({ id: existing.id, location: toPoint(p) });
    else setDraft((d) => (d?.kind === "poi" ? { ...d, location: toPoint(p) } : d));
    mapRef.current?.flyTo(p, 17);
  };
  /** SW-RS-21: a new restriction of the same kind, starting when the previous one ends */
  const chainAfter = (prev: Restriction) => {
    if (!prev.endAt) return;
    const preset: Partial<Restriction> = { startAt: prev.endAt, crossable: prev.crossable, deviceType: prev.deviceType, maxHeightM: prev.maxHeightM, oldDirection: prev.oldDirection, label: prev.label, comment: prev.comment };
    startRestriction(prev.kind, preset);
    toast.show(fr.restriction.chainStarted(formatDateTime(prev.endAt)));
  };
  /** SW-HI-03: an entry of the history points at an object that still exists */
  const showFromHistory = (entityType: string, entityId: string): boolean => {
    const g = entityType === "RESTRICTION" ? e.restrictions?.find((r) => r.id === entityId)?.geometry : entityType === "POI" ? e.pois?.find((p) => p.id === entityId)?.location : undefined;
    if (!g) return false;
    setDraft(null);
    setSelection({ type: entityType === "POI" ? "poi" : "restriction", id: entityId });
    mapRef.current?.fit(geometryPoints(g));
    return true;
  };
  const draftRestriction = (): Restriction | null => {
    if (draft?.kind !== "restriction" || !draft.geometry) return null;
    if (draft.existing) return { ...draft.existing, geometry: draft.geometry };
    return { id: "", kind: draft.restrictionKind, enabled: true, ...draft.preset, geometry: draft.geometry };
  };
  const liveTitle = live.mode === "live" ? fr.workspace.liveOk : live.mode === "polling" ? fr.workspace.livePolling : fr.workspace.liveOffline;

  return (
    <div className="workspace edit">
      <aside className="panel">
        <header className="ws-head">
          <Link to="/" className="small">← {fr.nav.events}</Link>
          <div className="row between wrap">
            <h2 className="event-name">{e.name}</h2>
            <span className="row tight"><PhaseBadge phase={phase(e, serverNow())} /><StatusBadge status={e.status ?? "DRAFT"} /></span>
          </div>
          <div className="muted small row wrap">
            <span>{fr.workspace.revision(e.revision ?? 0)}</span>
            <span className={"live-dot " + live.mode} title={liveTitle} data-mode={live.mode} />
            {live.lastSyncAt && <span title={liveTitle}>{fr.workspace.lastSync(formatTime(live.lastSyncAt))}</span>}
            {s.can("route.test") && <Link to={`/test?event=${encodeURIComponent(id)}`}>{fr.nav.routeTest}</Link>}
          </div>
          <div className="field header-time">
            <span>{fr.workspace.statusAt}</span>
            <div className="row">
              <input type="datetime-local" aria-label={fr.workspace.statusAt} value={toParisInput(atIso)} onChange={(ev) => ev.target.value && setAt(fromParisInput(ev.target.value))} />
              <button className={simulated ? "" : "active"} onClick={() => setAt(null)}>{fr.route.now}</button>
            </div>
            {trans.length > 0 && (
              <div className="transitions" title={fr.workspace.transitionsHint}>
                <span className="muted small">{fr.route.transitions} :</span>
                {trans.slice(0, 12).map((t) => <button key={t} className={`chip ${t === atIso ? "active" : ""}`} title={formatDateTime(t)} onClick={() => setAt(t)}>{formatTime(t)}</button>)}
              </div>
            )}
          </div>
          {readOnly && <Notice kind="warn">{fr.workspace.readOnly}</Notice>}
          {!readOnly && activeNow && <Notice kind="warn">{fr.workspace.activeWarning}</Notice>}
          {!readOnly && !activeNow && e.status === "PUBLISHED" && <div className="muted small">{fr.workspace.diffusion}</div>}
        </header>
        <nav className="tabs">
          {TABS.map((t) => <button key={t} className={tab === t ? "active" : ""} onClick={() => setTab(t)}>{fr.workspace.tabs[t]}</button>)}
        </nav>
        {mode !== "select" && (
          <Notice kind="info">
            {mode === "point" ? fr.workspace.draw.pointHint : mode === "poi" ? fr.workspace.draw.poiHint : mode === "polygon" ? fr.workspace.draw.zoneHint : fr.workspace.draw.pathHint}
            <div className="row">
              {(mode === "path" || mode === "polygon") && <button className="primary" disabled={draftCount < (mode === "path" ? 2 : 3)} onClick={() => mapRef.current?.finish()}>{fr.workspace.draw.stop}</button>}
              {(mode === "path" || mode === "polygon") && draftCount > 0 && <button onClick={() => mapRef.current?.undoVertex()}>↶</button>}
              <button onClick={() => { mapRef.current?.cancel(); stopDrawing(); setDraft(null); }}>{fr.workspace.draw.cancel}</button>
            </div>
          </Notice>
        )}

        {tab === "objects" && (
          <section className="stack">
            {draft?.kind === "restriction" && draft.geometry && (
              <RestrictionForm eventId={id} event={e} restriction={draftRestriction()!}
                isNew={!draft.existing} activeNow={activeNow}
                onRedraw={() => { setDraft({ ...draft, geometry: undefined }); setMode(pointKind(draft.restrictionKind) ? "point" : "path"); }}
                onMoveTo={(p) => moveRestrictionTo(p)} onChain={chainAfter} onSnapLine={setSnapLine}
                onDone={async (msg, saved) => { setDraft(null); setSelection(null); flashGeometry(saved?.geometry); await afterWrite(msg); }} onCancel={() => setDraft(null)} />
            )}
            {draft?.kind === "poi" && draft.location && (
              <PoiForm eventId={id} event={e} poi={draft.existing ? { ...draft.existing, location: draft.location } : { id: "", categoryId: e.poiCategories?.[0]?.id ?? "", name: "", location: draft.location }}
                isNew={!draft.existing} activeNow={activeNow}
                onMove={() => { setDraft({ ...draft, location: undefined }); setMode("poi"); }} onLocationChange={(p) => movePoiTo(p)}
                onDone={async (msg, saved) => { setDraft(null); setSelection(null); flashGeometry(saved?.location); await afterWrite(msg); }} onCancel={() => setDraft(null)} />
            )}
            {!draft && selectedRestriction && (
              <RestrictionForm key={selectedRestriction.id} eventId={id} event={e} restriction={selectedRestriction} activeNow={activeNow} readOnly={!canWrite}
                onRedraw={() => { setDraft({ kind: "restriction", restrictionKind: selectedRestriction.kind, existing: selectedRestriction }); setMode(selectedRestriction.geometry.type === "Point" ? "point" : "path"); }}
                onMoveTo={(p) => moveRestrictionTo(p, selectedRestriction)} onChain={canWrite ? chainAfter : undefined} onSnapLine={setSnapLine}
                onDone={async (msg, saved) => { setSelection(null); flashGeometry(saved?.geometry); await afterWrite(msg); }} onCancel={() => setSelection(null)} />
            )}
            {!draft && selectedPoi && (
              <PoiForm key={selectedPoi.id} eventId={id} event={e} poi={poiMoved?.id === selectedPoi.id ? { ...selectedPoi, location: poiMoved.location } : selectedPoi} activeNow={activeNow} readOnly={readOnly || !s.can("poi.write")}
                onMove={() => { setDraft({ kind: "poi", existing: selectedPoi }); setMode("poi"); }} onLocationChange={(p) => movePoiTo(p, selectedPoi)}
                onDone={async (msg, saved) => { setSelection(null); flashGeometry(saved?.location); await afterWrite(msg); }} onCancel={() => setSelection(null)} />
            )}
            {!draft && selectedHc && (
              <div className="card">
                <h3>{fr.workspace.hc}</h3>
                <div>{selectedHc.maxHeightM.toFixed(2).replace(".", ",")} m</div>
                <p className="small muted">{selectedHc.comment}</p>
                {s.isAdmin && <Link to="/admin/heights" className="small">{fr.admin.tabs.heights}</Link>}
              </div>
            )}
            {!draft && !selection && (
              <>
                {canWrite && (
                  <div className="toolbar wrap">
                    {(["BARRIER", "HEIGHT_LIMIT", "ONEWAY_LIFTED", "ONEWAY_REVERSED", "EMERGENCY_ONLY", "COURSE", "CROSSING_POINT"] as RestrictionKind[]).map((k) => {
                      const noCourse = k === "CROSSING_POINT" && !(e.restrictions ?? []).some((r) => r.kind === "COURSE");
                      return <button key={k} disabled={noCourse} title={noCourse ? fr.restriction.crossingPointNoCourse : fr.restriction.kindHelp[k]} onClick={() => startRestriction(k)}>+ {fr.kind[k]}</button>;
                    })}
                    {s.can("poi.write") && <button onClick={startPoi} disabled={(e.poiCategories ?? []).length === 0} title={(e.poiCategories ?? []).length === 0 ? fr.poiForm.noCategory : ""}>+ POI</button>}
                    {s.can("import.run") && <button onClick={() => setImportOpen(true)}>{fr.workspace.import}</button>}
                  </div>
                )}
                {outside > 0 && <div className="warn small">{fr.workspace.objectsOutside(outside)}</div>}
                <ObjectList event={e} canWrite={canWrite} at={at} onSelect={(sel) => { setSelection(sel); const g = sel.type === "restriction" ? e.restrictions?.find((r) => r.id === sel.id)?.geometry : e.pois?.find((p) => p.id === sel.id)?.location; if (g) mapRef.current?.fit(geometryPoints(g)); }}
                  onChanged={() => afterWrite()} />
              </>
            )}
          </section>
        )}
        {tab === "categories" && <CategoriesPanel eventId={id} event={e} readOnly={readOnly || !s.can("poi.write")} onChanged={afterWrite} />}
        {tab === "history" && <HistoryPanel eventId={id} revision={e.revision ?? 0} onShow={showFromHistory} />}
        {tab === "settings" && (
          <SettingsPanel eventId={id} event={e} activeNow={activeNow} global={live.global} zonePending={zonePending} zoneDrawing={zoneDrawing}
            onDrawZone={startZone} onZoneApplied={() => setZonePending(null)} onCancelZone={() => { setZonePending(null); mapRef.current?.cancel(); stopDrawing(); }}
            onChanged={afterWrite} onDeleted={() => navigate("/")} />
        )}
        {tab === "share" && <SharePanel eventId={id} event={e} />}
      </aside>

      <div className="map-area">
        <EditMap ref={mapRef} basemapUrl={live.pack?.basemapUrl} bbox={live.pack?.bbox} packs={packAreas} symbology={symbology} drawn={drawnFc} pois={poisData} hc={hcData} overlay={overlay}
          zone={zoneData} zoneAdjusted={zonePending ? polygonFc(zonePending) : zoneAdjData} visible={visible} mode={mode} preview={importPreview ?? undefined} flash={flash}
          onSelect={(sel) => { if (!draft) { setSelection(sel); if (sel) setTab("objects"); } }} onPoint={onPoint} onPath={onPath} onPolygon={onPolygon} onDraftChange={setDraftCount} />
        <div className="map-tools">
          <SearchBox near={e.zone ? { lat: e.zone.coordinates[0][0][1], lon: e.zone.coordinates[0][0][0] } : null} eventId={e.id}
            onPick={(p, h) => { mapRef.current?.flyTo(p, 16); mapRef.current?.showSearchResult({ position: p, label: h.label, detail: hitDetail(h) }); }}
            onClear={() => mapRef.current?.showSearchResult(null)} />
        </div>
        <MapLegend groups={legendGroups(visible, symbology, e.poiCategories)} onToggle={(k, v) => setVisible({ ...visible, [k]: v })} />
        {mode !== "select" && <div className="sim-banner">{mode === "polygon" ? fr.workspace.draw.zoneHint : mode === "path" ? fr.workspace.draw.pathHint : mode === "poi" ? fr.workspace.draw.poiHint : fr.workspace.draw.pointHint}</div>}
        {mode === "select" && simulated && <div className="sim-banner">{fr.route.simulatedBanner(formatDateTime(atIso))}</div>}
      </div>
      {importOpen && (
        <Suspense fallback={null}>
          <ImportDialog open eventId={id} event={e} onClose={() => setImportOpen(false)} onPreview={setImportPreview}
            onDone={async (msg) => { setImportOpen(false); await afterWrite(msg); }} />
        </Suspense>
      )}
      {toast.el}
    </div>
  );
}

/** legend of the editor map (C1): what the operator entered, the effects, the POI categories, the corrections, the zone */
function legendGroups(visible: typeof DEFAULT_VISIBLE, S: Styles, categories: EventData["poiCategories"]): LegendGroup[] {
  const lineOf = (color: string, dashed = false) => <span className="legend-line" style={{ borderTopColor: color, borderTopStyle: dashed ? "dashed" : "solid" }} />;
  return [
    { key: "drawn", label: fr.workspace.drawn, visible: visible.drawn, entries: [
      { key: "nc", label: fr.legend.barrierNonCrossable, swatch: dotSwatch(S.BARRIER_NON_CROSSABLE.color, "×") },
      { key: "c", label: fr.legend.barrierCrossable, swatch: dotSwatch(S.BARRIER_CROSSABLE.color, "+") },
      { key: "h", label: fr.legend.heightLimit, swatch: dotSwatch(S.HEIGHT_OVERRIDE.color, "h") },
      { key: "l", label: fr.legend.ONEWAY_LIFTED, swatch: lineOf(S.ONEWAY_LIFTED.color) },
      { key: "r", label: fr.legend.ONEWAY_REVERSED, swatch: lineOf(S.ONEWAY_REVERSED.color) },
      { key: "e", label: fr.legend.emergencyOnly, swatch: lineOf(S.EMERGENCY_ACCESS.color) },
      { key: "p", label: fr.legend.course, swatch: lineOf(S.COURSE.color) },
    ] },
    { key: "effects", label: fr.workspace.effects, visible: visible.effects, entries: effectEntries(S) },
    { key: "pois", label: fr.workspace.pois, visible: visible.pois, entries: categoryEntries(categories, S.POI.color) },
    { key: "hc", label: fr.workspace.hc, visible: visible.hc, entries: [{ key: "hc", label: fr.legend.hc, swatch: dotSwatch("#495057") }] },
    { key: "zone", label: fr.workspace.zone, visible: visible.zone, entries: [
      { key: "z", label: fr.legend.zone, swatch: lineOf("#1d3a8a") },
      { key: "za", label: fr.legend.zoneAdjusted, swatch: lineOf("#1d3a8a", true) },
    ] },
  ];
}
